import React from 'react';
import { format } from 'date-fns';
import { pl, enUS } from 'date-fns/locale';
import { useLanguage } from '@/hooks/useLanguage';
import { DatabaseIcon } from 'lucide-react';
import { LeakItem } from '@/types/dashboard';
import { Link } from 'react-router-dom';

interface LeakItemProps {
  leak: LeakItem;
  index?: number; // Added index for fallback ID
}

export function LeakListItem({ leak, index = 0 }: LeakItemProps) {
  const { language } = useLanguage();

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'dd MMM yyyy', {
        locale: language === 'pl' ? pl : enUS,
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatNumber = (num?: number) => {
    if (num === undefined) return '0';
    return new Intl.NumberFormat(language === 'pl' ? 'pl-PL' : 'en-US').format(num);
  };

  return (
    <Link to={`/leaks/${leak.domain || `unknown-${index}`}`} className="block h-full group">
      <div className="cursor-pointer hover:bg-secondary/30 p-2 rounded-md transition-colors h-full flex flex-col justify-center">
        <div className="flex flex-col w-full">
          <div className="flex justify-between items-center mb-1 shrink-0">
            <h3 className="font-medium line-clamp-1 group-hover:text-primary">
              {leak.domain || 'Unknown Source'}
            </h3>
            {leak.leak_count && (
              <span className="text-xs text-muted-foreground flex items-center shrink-0">
                <DatabaseIcon className="h-3 w-3 mr-1" />
                {formatNumber(leak.leak_count)}
              </span>
            )}
          </div>
          <div className="flex justify-between text-xs text-muted-foreground mt-auto pt-1 shrink-0">
            <span className="line-clamp-1">
              {language === 'pl' && leak.data_leaked_pl ? leak.data_leaked_pl : leak.data_leaked}
            </span>
            <span className="shrink-0 ml-2">{formatDate(leak.breach_date)}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}
